import { ALL_FACES } from '../cards';
import { DIAGONAL, ORTHOGONAL, onBoard, onBoardPawns, opponent, sameSquare } from '../board';
import type { Action, Epitaph, MatchEvent, MatchState, Side, Square } from '../types';
import { checkEpitaphReplay } from '../rules';
import { returnPawn, mirrorInscription, type Draw } from './flow';
import { includes, TABLE_CURIOS, type TableCurioId } from './ids';
import { L, addSideStatus, epitaphSlots, handLimit, setStatus, untilOwnersNextTurn } from './state';
import type { CurioTarget, ItemSpec } from './types';

/**
 * Table Curios (LEDGER.MD): one-use items a side uses during its own turn, at any point, without
 * ending the turn. The Planchette is the exception: its replay is the turn's action.
 */

type UseCurio = Extract<Action, { type: 'USE_CURIO' }>;
type Events = MatchEvent[];

const chebyshev = (a: Square, b: Square): number =>
  Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));

export const curiosOf = (s: Pick<MatchState, 'ledger'>, side: Side): readonly ItemSpec[] =>
  s.ledger?.curios[side] ?? [];

const occupiedAt = (s: MatchState, sq: Square): boolean =>
  Object.values(s.pawns).some((p) => p.status === 'onBoard' && sameSquare(p.square, sq)) ||
  (s.ledger?.omens ?? []).some((o) => o.kind === 'pillar' && !o.spent && sameSquare(o.square, sq));

/** Whether a side may use Curios now: its own turn, before or during its action. */
export const curioWindow = (s: MatchState, side: Side): boolean =>
  !!s.ledger &&
  s.decider === side &&
  s.active === side &&
  (s.phase === 'TURN_START' || s.phase === 'ACTION' || s.phase === 'CHAIN');

/** Squares an enemy pawn may be pulled to by a Lodestone. */
function lodestoneSquares(s: MatchState, side: Side, enemySq: Square): Square[] {
  const own = onBoardPawns(s.pawns, side).flatMap((p) => (p.square ? [p.square] : []));
  if (own.length === 0) return [];
  const nearest = (sq: Square) => Math.min(...own.map((o) => chebyshev(o, sq)));
  const now = nearest(enemySq);
  const out: Square[] = [];
  for (const d of [...ORTHOGONAL, ...DIAGONAL]) {
    const to = { x: enemySq.x + d.dx, y: enemySq.y + d.dy };
    if (!onBoard(to.x, to.y) || occupiedAt(s, to)) continue;
    if (nearest(to) < now) out.push(to);
  }
  return out;
}

/** Every legal USE_CURIO action for a side (§16.6 extended). */
export function curioActions(s: MatchState, side: Side): UseCurio[] {
  if (!curioWindow(s, side)) return [];
  const out: UseCurio[] = [];
  const seen = new Set<string>();
  const enemies = onBoardPawns(s.pawns, opponent(side));
  const own = onBoardPawns(s.pawns, side);
  for (const item of curiosOf(s, side)) {
    if (seen.has(item.id)) continue; // two of the same Curio offer the same choices
    seen.add(item.id);
    const uid = item.uid;
    const add = (target?: CurioTarget) =>
      out.push(
        target ? { type: 'USE_CURIO', side, uid, target } : { type: 'USE_CURIO', side, uid },
      );
    switch (item.id as TableCurioId) {
      case 'chalk':
        for (const face of ALL_FACES) add({ face });
        break;
      case 'hourglass':
      case 'black-candle':
      case 'rag-doll':
        add();
        break;
      case 'pocket-watch':
        if (s.players[side].deck.length > 0 && s.players[side].hand.length < handLimit(s, side))
          add();
        break;
      case 'pliers':
      case 'matchbook':
        for (const p of enemies) add({ pawnId: p.id });
        break;
      case 'scissors':
        for (const p of enemies) if (p.inscription) add({ pawnId: p.id });
        break;
      case 'bell-jar':
        for (const p of own) add({ pawnId: p.id });
        break;
      case 'smelling-salts':
        for (const p of Object.values(s.pawns))
          if (p.side === side && p.status !== 'onBoard' && !occupiedAt(s, p.startSquare))
            add({ pawnId: p.id });
        break;
      case 'lodestone':
        for (const p of enemies)
          if (p.square)
            for (const to of lodestoneSquares(s, side, p.square)) add({ pawnId: p.id, to });
        break;
      case 'planchette':
        if (s.phase === 'ACTION')
          for (const id of Object.keys(L(s).pileEpitaphs)) {
            const epitaph = planchetteEpitaph(s, side, id);
            if (!epitaph) continue;
            for (const p of own)
              if (checkEpitaphReplay(s, side, epitaph, p.id).ok)
                add({ epitaphId: id, pawnId: p.id });
          }
        break;
      case 'cracked-mirror': {
        const theirs = s.players[opponent(side)].epitaphs;
        if (s.players[side].epitaphs.length < epitaphSlots(s, side))
          for (const e of theirs) add({ epitaphId: e.id });
        break;
      }
      case 'spyglass':
        if (s.players[side].deck.length > 0)
          for (const cardId of s.players[side].hand) add({ cardId });
        break;
    }
  }
  return out;
}

/** The Epitaph a Planchette would replay: one on the pile, mirrored when it is the other side's. */
export function planchetteEpitaph(s: MatchState, side: Side, epitaphId: string): Epitaph | null {
  const e = L(s).pileEpitaphs[epitaphId];
  if (!e) return null;
  return {
    id: `${e.id}-P${s.seq}`,
    owner: side,
    sourcePawnId: e.sourcePawnId,
    inscription: e.owner === side ? e.inscription : mirrorInscription(e.inscription),
  };
}

/** Checks a USE_CURIO action against curioActions; returns an error message or null. */
export function curioError(s: MatchState, a: UseCurio): string | null {
  if (!s.ledger) return 'Curios exist only at the Long Night';
  if (!curiosOf(s, a.side).some((c) => c.uid === a.uid)) return `${a.side} holds no Curio ${a.uid}`;
  if (!curioWindow(s, a.side)) return 'Curios are used during your own turn';
  const item = curiosOf(s, a.side).find((c) => c.uid === a.uid) as ItemSpec;
  if (!includes(TABLE_CURIOS, item.id)) return `${item.id} is not used at the table`;
  const same = curioActions(s, a.side).filter(
    (x) => curiosOf(s, a.side).find((c) => c.uid === x.uid)?.id === item.id,
  );
  const t = a.target;
  const match = same.some((x) => {
    const u = x.target;
    if (!u && !t) return true;
    if (!u || !t) return false;
    return (
      u.pawnId === t.pawnId &&
      u.epitaphId === t.epitaphId &&
      u.cardId === t.cardId &&
      (u.face
        ? !!t.face && u.face.color === t.face.color && u.face.number === t.face.number
        : !t.face) &&
      (u.to ? !!t.to && sameSquare(u.to, t.to) : !t.to)
    );
  });
  return match ? null : 'that Curio cannot be used that way now';
}

/**
 * Uses a validated Curio. Returns 'replay' for a Planchette, whose replay the caller resolves as
 * the turn's action, else 'done'.
 */
export function useCurio(
  s: MatchState,
  a: UseCurio,
  ev: Events,
  draw: Draw,
):
  | { readonly kind: 'done' }
  | { readonly kind: 'replay'; readonly epitaph: Epitaph; readonly pawnId: string } {
  const l = L(s);
  const side = a.side;
  const item = l.curios[side].find((c) => c.uid === a.uid) as ItemSpec;
  s.ledger = {
    ...l,
    curios: { ...l.curios, [side]: l.curios[side].filter((c) => c.uid !== a.uid) },
  };
  ev.push({ type: 'CurioUsed', side, uid: a.uid, curio: item.id, target: a.target ?? null });
  const t = a.target ?? {};
  const pawnId = t.pawnId as string;
  switch (item.id as TableCurioId) {
    case 'chalk':
      if (t.face) s.pile.push({ source: 'chalk', face: t.face, playedBy: side });
      break;
    case 'hourglass': {
      const l2 = L(s);
      const opp = opponent(side);
      s.ledger = { ...l2, skipTurns: { ...l2.skipTurns, [opp]: l2.skipTurns[opp] + 1 } };
      break;
    }
    case 'pliers':
      setStatus(s, pawnId, { kind: 'pinned', turns: untilOwnersNextTurn(s, opponent(side)) }, ev);
      break;
    case 'scissors': {
      const p = s.pawns[pawnId];
      if (p) s.pawns[pawnId] = { ...p, inscription: null };
      setStatus(s, pawnId, { kind: 'hollow' }, ev);
      break;
    }
    case 'bell-jar':
      setStatus(s, pawnId, { kind: 'warded' }, ev);
      break;
    case 'smelling-salts':
      returnPawn(s, pawnId, 'returned', ev);
      break;
    case 'pocket-watch':
      for (let i = 0; i < 2; i++)
        if (s.players[side].hand.length < handLimit(s, side) && s.players[side].deck.length > 0)
          draw(s, side);
      break;
    case 'black-candle':
      addSideStatus(s, side, 'black-candle', 1);
      break;
    case 'rag-doll':
      addSideStatus(s, side, 'free-wild', 1);
      break;
    case 'lodestone': {
      const p = s.pawns[pawnId];
      if (p?.square && t.to) {
        s.pawns[pawnId] = { ...p, square: t.to };
        ev.push({
          type: 'PawnMoved',
          side: p.side,
          pawnId,
          from: p.square,
          to: t.to,
          cause: 'lodestone',
        });
      }
      break;
    }
    case 'matchbook':
      setStatus(s, pawnId, { kind: 'smoldering', n: 2 }, ev);
      break;
    case 'cracked-mirror': {
      const e = s.players[opponent(side)].epitaphs.find((x) => x.id === t.epitaphId);
      if (e) {
        const copy: Epitaph = {
          id: `EP${s.nextEpitaphSeq}`,
          owner: side,
          sourcePawnId: e.sourcePawnId,
          inscription: mirrorInscription(e.inscription),
        };
        s.nextEpitaphSeq += 1;
        s.players[side].epitaphs.push(copy);
        ev.push({ type: 'EpitaphReceived', side, epitaph: copy, how: 'chosen' });
      }
      break;
    }
    case 'spyglass': {
      const pl = s.players[side];
      const cardId = t.cardId as string;
      const i = pl.hand.indexOf(cardId);
      if (i >= 0 && pl.deck.length > 0) {
        pl.hand.splice(i, 1);
        draw(s, side);
        pl.deck.push(cardId);
      }
      break;
    }
    case 'planchette': {
      const epitaph = planchetteEpitaph(s, side, t.epitaphId as string);
      if (epitaph) return { kind: 'replay', epitaph, pawnId };
      break;
    }
  }
  return { kind: 'done' };
}
